import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Clock, MapPin, Warning, ChatCircleText, FunnelSimple, MagnifyingGlass } from '@phosphor-icons/react';

const statusColors = {
  'Yeni': 'bg-[hsl(var(--primary))] text-white',
  'İnceleniyor': 'bg-[hsl(var(--accent))] text-black',
  'İşleme Alındı': 'bg-blue-600 text-white',
  'Kapatıldı': 'bg-[hsl(var(--success))] text-white',
};

export default function MyReportsPage() {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [cities, setCities] = useState([]);
  const [cityFilter, setCityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef(null);

  const handleSearchChange = (val) => {
    setSearchTerm(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(val.length >= 3 ? val : '');
    }, 300);
  };

  const fetchReports = useCallback(() => {
    setLoading(true);
    const params = {};
    if (cityFilter !== 'all') params.city = cityFilter;
    if (searchQuery) params.search = searchQuery;
    authAxios().get('/reports', { params })
      .then(res => { setReports(res.data.reports); setTotal(res.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authAxios, cityFilter, searchQuery]);

  useEffect(() => {
    authAxios().get('/reports/cities').then(res => setCities(res.data)).catch(() => {});
  }, [authAxios]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="bg-[#1C1C1E] border-b border-[#2C2C2E] px-4 py-3 flex items-center gap-3">
        <Button
          data-testid="back-button"
          variant="ghost"
          onClick={() => navigate('/reporter')}
          className="text-white hover:bg-[#2C2C2E] h-10 w-10 p-0"
        >
          <ArrowLeft weight="bold" className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2">
          <Warning weight="fill" className="w-5 h-5 text-[hsl(var(--primary))]" />
          <h1 className="font-heading text-lg text-white">İHBARLARIM</h1>
          {!loading && (
            <span className="ml-2 inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full bg-[hsl(var(--primary))] text-white text-xs font-bold">{total}</span>
          )}
        </div>
      </header>

      <main className="p-4 max-w-2xl mx-auto">
        {/* Search */}
        <div className="relative mb-3">
          <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
          <Input
            data-testid="search-input"
            value={searchTerm}
            onChange={e => handleSearchChange(e.target.value)}
            placeholder="İhbar no, kurum, şehir ara... (min 3 karakter)"
            className="h-10 pl-10 bg-[#1C1C1E] border-[#2C2C2E] text-white placeholder:text-[#666] text-sm"
          />
        </div>

        {/* City Filter */}
        {cities.length > 0 && (
          <div className="flex items-center gap-3 mb-4">
            <FunnelSimple weight="bold" className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger data-testid="city-filter" className="w-44 h-9 bg-[#1C1C1E] border-[#2C2C2E] text-white text-xs">
                <SelectValue placeholder="Şehir filtrele" />
              </SelectTrigger>
              <SelectContent className="bg-[#1C1C1E] border-[#2C2C2E]">
                <SelectItem value="all" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white text-xs">Tüm Şehirler</SelectItem>
                {cities.map(c => (
                  <SelectItem key={c} value={c} className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white text-xs">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {loading ? (
          <div className="text-center py-12 text-[hsl(var(--muted-foreground))]">Yükleniyor...</div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[hsl(var(--muted-foreground))]">Henüz ihbar göndermediniz</p>
            <Button
              data-testid="create-first-report-button"
              onClick={() => navigate('/reporter')}
              className="mt-4 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white h-12"
            >
              İlk İhbarını Gönder
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map(report => (
              <Card key={report.id} data-testid={`report-card-${report.id}`} className="bg-[#1C1C1E] border-[#2C2C2E]">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-heading text-white text-sm">{report.report_no}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{report.institution_name}</p>
                    </div>
                    <Badge className={`${statusColors[report.status] || 'bg-[#2C2C2E] text-white'} text-xs`}>
                      {report.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[hsl(var(--muted-foreground))]">
                    <span className="flex items-center gap-1">
                      <Clock weight="bold" className="w-3 h-3" />
                      {formatDate(report.created_at)}
                    </span>
                    {report.latitude && (
                      <button
                        data-testid={`location-link-${report.id}`}
                        onClick={() => {
                          const label = report.report_no.slice(-6);
                          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                          const url = isIOS
                            ? `maps://maps.apple.com/?q=${encodeURIComponent(label)}&ll=${report.latitude},${report.longitude}&z=17`
                            : `https://www.google.com/maps?q=${report.latitude},${report.longitude}+(${label})`;
                          window.open(url, '_blank');
                        }}
                        className="flex items-center gap-1 text-[hsl(var(--primary))] hover:text-white transition-colors"
                      >
                        <MapPin weight="fill" className="w-3.5 h-3.5" />
                        <span className="underline underline-offset-2">{report.city || 'Konumu Gör'}</span>
                      </button>
                    )}
                  </div>
                  {report.last_note && (
                    <div className="mt-2 flex items-start gap-2 bg-[#0A0A0A] rounded-md px-3 py-2">
                      <ChatCircleText weight="fill" className="w-4 h-4 text-[hsl(var(--accent))] shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-xs text-[hsl(var(--accent))] font-medium">{report.last_note_status}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{report.last_note}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
